import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { showErrorToast, showSuccessToast } from '../../util/toast';
import { SendgridEmailTemplate, StatusCode } from '../../util/enums';
import { sendEmailNotification } from '../../util/api';
import { createSlug } from '../../util/urlHelpers';
import { entityRefs, isArrayLength } from '../../util/genericHelpers';
import { denormalisedEntities, updatedEntities } from '../../util/data';
import { EDIT } from '../../util/constants';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const CREATE_JOB_LISTING_REQUEST = 'app/JobPostPage/CREATE_JOB_LISTING_REQUEST';
export const CREATE_JOB_LISTING_SUCCESS = 'app/JobPostPage/CREATE_JOB_LISTING_SUCCESS';
export const CREATE_JOB_LISTING_ERROR = 'app/JobPostPage/CREATE_JOB_LISTING_ERROR';

export const SEND_JOB_INVITE_REQUEST = 'app/JobPostPage/SEND_JOB_INVITE_REQUEST';
export const SEND_JOB_INVITE_SUCCESS = 'app/JobPostPage/SEND_JOB_INVITE_SUCCESS';
export const SEND_JOB_INVITE_ERROR = 'app/JobPostPage/SEND_JOB_INVITE_ERROR';

export const SHOW_LISTING_REQUEST = 'app/JobPostPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/JobPostPage/SHOW_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  createJobListingInProgress: false,
  createJobListingError: null,
  sendJobInviteInProgress: false,
  sendJobInviteError: null,
  id: null,
  showListingError: null,
};

const JobPostPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case CREATE_JOB_LISTING_REQUEST:
      return { ...state, createJobListingInProgress: true, createJobListingError: null };
    case CREATE_JOB_LISTING_SUCCESS:
      return { ...state, createJobListingInProgress: false, createJobListingError: null };
    case CREATE_JOB_LISTING_ERROR:
      return { ...state, createJobListingInProgress: false, createJobListingError: payload };
    case SEND_JOB_INVITE_REQUEST:
      return { ...state, sendJobInviteInProgress: true, sendJobInviteError: null };
    case SEND_JOB_INVITE_SUCCESS:
      return { ...state, sendJobInviteInProgress: false, sendJobInviteError: null };
    case SEND_JOB_INVITE_ERROR:
      return { ...state, sendJobInviteInProgress: false, sendJobInviteError: payload };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };
    default:
      return state;
  }
};

export default JobPostPageReducer;

// ================ Selectors ================ //
export const createJobListingSelector = state => {
  const { createJobListingInProgress, createJobListingError } = state.JobPostPage;
  return {
    createJobListingInProgress,
    createJobListingError,
  };
};
export const sendJobInviteSelector = state => {
  const { sendJobInviteInProgress, sendJobInviteError } = state.JobPostPage;
  return {
    sendJobInviteInProgress,
    sendJobInviteError,
  };
};

// ================ Action creators ================ //

export const sendJobInviteRequest = () => ({ type: SEND_JOB_INVITE_REQUEST });
export const sendJobInviteSuccess = () => ({
  type: SEND_JOB_INVITE_SUCCESS,
});
export const sendJobInviteError = error => ({
  type: SEND_JOB_INVITE_ERROR,
  error: true,
  payload: error,
});

export const createJobListingRequest = () => ({ type: CREATE_JOB_LISTING_REQUEST });
export const createJobListingSuccess = () => ({
  type: CREATE_JOB_LISTING_SUCCESS,
});
export const createJobListingError = error => ({
  type: CREATE_JOB_LISTING_ERROR,
  error: true,
  payload: error,
});
export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const createJobListing = params => async (dispatch, getState, sdk) => {
  dispatch(createJobListingRequest());
  const { id } = params || {};
  try {
    const recipientEmails = params?.privateData?.recipientEmails || [];
    const response = id
      ? await sdk.ownListings.update(params)
      : await sdk.ownListings.create(params);
    const slug = createSlug(params.title);
    const listingId = response?.data?.data?.id?.uuid;
    if (isArrayLength(recipientEmails)) {
      dispatch(
        sendJobInvite({
          templateId: SendgridEmailTemplate.JOB_INVITE,
          recipientEmail: recipientEmails,
          templateData: {
            jobTitle: params?.title,
            jobLink: `${process.env.REACT_APP_MARKETPLACE_ROOT_URL}/${slug}/${listingId}`,
            // add more template data later
          },
        })
      );
    }
    dispatch(createJobListingSuccess());
    showSuccessToast('Job post created successfully');
    return response?.data?.data;
  } catch (error) {
    dispatch(createJobListingError(storableError(error)));
  }
};

export const sendJobInvite = params => async dispatch => {
  dispatch(sendJobInviteRequest());
  try {
    const response = await sendEmailNotification(params);
    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(sendJobInviteSuccess());
      showSuccessToast('Successfully sent email');
    }
  } catch (error) {
    showErrorToast('Error while sending email');
    dispatch(sendJobInviteError(storableError(error)));
  }
};

export const showListing = (listingId, config) => async (dispatch, getState, sdk) => {
  // Destructure layout config with defaults
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Dispatch initial actions
  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());

  // Build the query parameters
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'author.publicData', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
      // Social media
      'variants.facebook',
      'variants.twitter',
      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  try {
    const data = await sdk.ownListings.show(params);
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };
    dispatch(addMarketplaceEntities(data, sanitizeConfig));
    return data;
  } catch (error) {
    dispatch(showListingError(storableError(error)));
  }
};

export const loadData = (params, search, config) => dispatch => {
  const listingId = new UUID(params.id);
  const type = params?.type;
  if (type === EDIT) {
    dispatch(showListing(listingId, config));
  }
  return dispatch(fetchCurrentUser());
};
